import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SERVICE_OPTIONS, PHONE } from "@/lib/site";
import { Loader2, CalendarCheck, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "h-12 rounded-none bg-[#0a0a0a] border border-[#222] text-white placeholder:text-[#666] focus-visible:ring-1 focus-visible:ring-[#FF4D00] focus-visible:ring-offset-0 focus-visible:border-[#FF4D00]";

const TIMES = ["8:00 AM – 10:00 AM", "10:00 AM – 12:00 PM", "12:00 PM – 2:00 PM", "2:00 PM – 4:00 PM", "4:00 PM – 6:00 PM"];

const empty = { name: "", phone: "", email: "", service_type: "", preferred_date: "", preferred_time: "", address: "", notes: "" };

export default function BookingSection() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const today = new Date().toISOString().split("T")[0];

  const submit = async (e) => {
    e.preventDefault();
    const req = ["name", "phone", "email", "service_type", "preferred_date", "preferred_time", "address"];
    if (req.some((k) => !form[k])) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/booking`, { ...form, notes: form.notes || null });
      toast.success("Booking received! We'll confirm your slot by phone.");
      setDone(true);
      setForm(empty);
    } catch (err) {
      toast.error("Booking failed. Please call us at " + PHONE);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" className="relative z-10 border-t border-[#222] bg-[#050505] px-4 py-20 md:px-12 lg:px-24 md:py-28">
      <div className="mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-12 gap-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <p className="font-mono-a text-xs tracking-[0.2em] uppercase text-[#FF4D00]">/ Book Online</p>
          <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-4 leading-[0.95]">
            Lock In Your <span className="text-[#FF4D00]">Pickup</span>
          </h2>
          <p className="text-[#888] text-base md:text-lg mt-5 max-w-md">
            Pick a day and a two-hour window. No deposits, no accounts. We'll call to confirm before we roll out.
          </p>
          <div className="mt-8 space-y-4 border-t border-[#222] pt-8">
            {["Upfront pricing, confirmed on site", "Same-day & next-day slots", "Family-owned, fully insured crew"].map((t) => (
              <div key={t} className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-[#FF4D00] shrink-0" />
                <span className="text-white/90 text-sm md:text-base">{t}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7 border border-[#222] bg-[#0d0d0d] p-6 md:p-8"
        >
          {done ? (
            <div data-testid="booking-success" className="flex flex-col items-start justify-center h-full py-10">
              <CheckCircle2 className="h-14 w-14 text-[#FF4D00]" />
              <h3 className="font-display text-3xl uppercase mt-4">You're On The Books</h3>
              <p className="text-[#888] mt-2 max-w-sm">We've got your request. One of the Guys will call {form.phone || "you"} shortly to confirm the details.</p>
              <button onClick={() => setDone(false)} className="mt-6 h-12 px-8 border border-[#FF4D00] text-[#FF4D00] font-mono-a uppercase tracking-widest text-sm hover:bg-[#FF4D00] hover:text-black transition-colors" data-testid="booking-again">
                Book Another Pickup
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Full Name *</Label>
                  <Input data-testid="booking-name" className={field} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Phone *</Label>
                  <Input data-testid="booking-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="(720) 000-0000" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Email *</Label>
                  <Input data-testid="booking-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Service *</Label>
                  <Select value={form.service_type} onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}>
                    <SelectTrigger data-testid="booking-service" className={field}><SelectValue placeholder="Select service" /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#222] text-white rounded-none">
                      {SERVICE_OPTIONS.map((o) => (
                        <SelectItem key={o} value={o} className="focus:bg-[#FF4D00] focus:text-black rounded-none">{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Preferred Date *</Label>
                  <Input data-testid="booking-date" type="date" min={today} className={`${field} [color-scheme:dark]`} value={form.preferred_date} onChange={set("preferred_date")} />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Time Window *</Label>
                  <Select value={form.preferred_time} onValueChange={(v) => setForm((f) => ({ ...f, preferred_time: v }))}>
                    <SelectTrigger data-testid="booking-time" className={field}><SelectValue placeholder="Select window" /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#222] text-white rounded-none">
                      {TIMES.map((o) => (
                        <SelectItem key={o} value={o} className="focus:bg-[#FF4D00] focus:text-black rounded-none">{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Pickup Address *</Label>
                <Input data-testid="booking-address" className={field} value={form.address} onChange={set("address")} placeholder="Street, City, CO" />
              </div>
              <div className="space-y-1.5">
                <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Notes</Label>
                <Textarea data-testid="booking-notes" className={`${field} h-20 py-3`} value={form.notes} onChange={set("notes")} placeholder="Gate code, stairs, what's being removed..." />
              </div>
              <button
                data-testid="booking-submit" type="submit" disabled={loading}
                className="w-full h-12 bg-[#FF4D00] hover:bg-[#FF6A00] text-black font-mono-a uppercase tracking-widest text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />}
                {loading ? "Booking..." : "Book My Pickup"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
